import * as React from 'react';
import propz from 'propz';
import { parse } from 'query-string';
import { Component } from 'react';
import { History, Location } from 'history';
import { AppUser } from 'Src/views/App/App';
import { Loader } from 'Src/components/Loader/Loader';
import { Pagination2 } from 'Src/components/Pagination/Pagination2';
import { AwardCard } from 'Src/components/AwardCard/AwardCard';
import { ParentFilterBar } from 'Src/components/ParentFilterBar/ParentFilterBar';
import { Child } from 'Src/models/child';
import { getChildrenAwardsCurrentOverview, AwardItem } from 'Src/helpers/service/parent/awards';
import {
  ColumnDefinition,
  getFilters2,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  getSearchFilter,
  getSearchOrder
} from 'Src/helpers/table/table';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE, CARD_LIMIT } from 'Src/consts/table';
import { ItemsBar } from 'Src/views/GenericView/ParentView/Calendar/ItemsBar';
import './Awards.scss';
import { ParentPageHeader } from '../components/ParentPageHeader';
import '../components/ParentLists.scss';

const DEFAULT_SEARCH = {
  order: 'awardedAt:DESC'
};

const FILTER_FIELDS_ALL: ColumnDefinition[] = [
  { text: 'Children', field: 'childIdList', type: FILTER_TYPE.MULTISELECT, accessor: [''], isSort: false },
  { text: 'Award type', field: 'awardType.name', type: FILTER_TYPE.TEXT, accessor: [''], isSort: false },
  { text: 'Awarded at', field: 'awardedAt', type: FILTER_TYPE.DATE_INTERVAL, accessor: [''], isSort: true }
];

const FILTER_FIELDS_PANEL: ColumnDefinition[] = [
  { text: 'Award type', field: 'awardType.name', type: FILTER_TYPE.TEXT, accessor: [''], isSort: false },
  { text: 'Awarded at', field: 'awardedAt', type: FILTER_TYPE.DATE_INTERVAL, accessor: [''], isSort: false }
];

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

interface State {
  items: AwardItem[];
  total: number;
  totalPoints: number;
  awardedChildren: number;
  currentPage: number;
  filters: any;
  sortFieldName: string;
  sortDirection: string;
  isFiltersOpen: boolean;
  isLoading: boolean;
  children: Child[];
  basePath: string;
}

export class ChildrenAwardsCurrent extends Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      total: 0,
      totalPoints: 0,
      awardedChildren: 0,
      currentPage: FIRST_PAGE,
      filters: {},
      sortFieldName: '',
      sortDirection: '',
      isFiltersOpen: false,
      isLoading: false,
      children: [],
      basePath: ''
    };
  }

  componentDidMount() {
    this.setState({ isLoading: true }, this.setItems);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({ isLoading: true }, this.setItems);
    }
  }

  setItems = () => {
    const { history, user } = this.props;
    const search = parse(history.location.search);

    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection = '';
    let sortFieldName = '';

    if (typeof search.order === 'string') {
      [sortFieldName, sortDirection] = (search.order as string).split(':');
    } else if (typeof DEFAULT_SEARCH.order === 'string') {
      [sortFieldName, sortDirection] = DEFAULT_SEARCH.order.split(':');
    }

    const filters = getFilters2(FILTER_FIELDS_ALL, search);
    const where = { ...filters };

    const order = DEFAULT_SEARCH.order.replace(':', ' ');
    const serverQueryFilter = getServerQueryFilter2(page, where, order, CARD_LIMIT);

    return getChildrenAwardsCurrentOverview(user, serverQueryFilter).then(overview => {
      const { items, total, points, awardedChildren, children } = overview;
      const hasChildSelection = Array.isArray(filters.childIdList) && filters.childIdList.length > 0;
      const filtersWithChild = hasChildSelection ? filters : { ...filters, childIdList: children.map(c => c.id) };

      this.setState({
        items,
        total,
        totalPoints: points,
        awardedChildren,
        children,
        currentPage: page,
        filters: filtersWithChild,
        sortDirection,
        sortFieldName,
        basePath: history.location.pathname,
        isLoading: false
      });
    });
  };

  setCurrentPageParams = (currentPage: number): void => {
    const { filters } = this.state;
    const parts: string[] = [];
    if (currentPage !== 1) parts.push(`page=${currentPage}`);
    if (isFilterExist2(filters)) parts.push(getSearchFilter(filters));
    this.props.history.push({ pathname: this.state.basePath, search: parts.join('&') });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = propz.get(event, ['target', 'value'], '');
    const { filters } = this.state;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = { ...nextFilters, [filterField]: { ...currentFilterField, from: filterValue } };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = { ...nextFilters, [filterField]: { ...currentFilterField, to: filterValue } };
          break;
      }
    } else {
      const filter = FILTER_FIELDS_PANEL.find(col => col.field === filterField);
      if (filter.type === FILTER_TYPE.MULTISELECT) {
        const opts = event.target.options;
        const value: string[] = [];
        for (let i = 0; i < opts.length; i++) if (opts[i].selected) value.push(opts[i].value);
        nextFilters = { ...nextFilters, [filterField]: value };
      } else {
        nextFilters = { ...nextFilters, [filterField]: filterValue };
      }
    }

    this.setState({ filters: nextFilters });
  };

  onApplyFilterClick = (): void => {
    const { filters } = this.state;
    const parts: string[] = [];
    if (isFilterExist2(filters)) parts.push(getSearchFilter(filters));
    this.props.history.push({ pathname: this.state.basePath, search: parts.join('&') });
  };

  onClearFilterClick = (): void => {
    this.props.history.push({ pathname: this.state.basePath, search: '' });
  };

  onChildPickerClick = (childId: string): void => {
    const { children, sortDirection, sortFieldName } = this.state;

    const nextFilters = {
      ...this.state.filters,
      childIdList: childId === 'all' ? children.map(c => c.id) : [childId]
    };

    this.setState({ filters: nextFilters }, () => {
      const parts: string[] = [];
      if (isSortExist(sortDirection, sortFieldName)) {
        parts.push(getSearchOrder(sortDirection, sortFieldName));
      }
      if (isFilterExist2(nextFilters)) {
        parts.push(getSearchFilter(nextFilters));
      }
      this.props.history.push({ pathname: this.state.basePath, search: parts.join('&') });
    });
  };

  render() {
    const {
      isLoading,
      items,
      total,
      totalPoints,
      awardedChildren,
      currentPage,
      filters,
      children,
      isFiltersOpen
    } = this.state;

    if (isLoading) return <Loader />;

    const selectedChildIds =
      filters.childIdList && filters.childIdList.length ? filters.childIdList : children.map(c => c.id);
    const from = total === 0 ? 0 : (currentPage - 1) * CARD_LIMIT + 1;
    const to = Math.min(currentPage * CARD_LIMIT, total);
    const isFiltered = isFilterExist2(filters);

    return (
      <main className="parentPage parentListPage awardsPage">
        <ParentPageHeader
          title="Awards & House Points"
          subtitle="Awards earned by your children during the current academic year."
          chips={[
            { value: total, label: total === 1 ? 'award this year' : 'awards this year', tone: 'amber' },
            { value: totalPoints, label: 'house points', tone: 'green' },
            {
              value: awardedChildren,
              label: awardedChildren === 1 ? 'awarded child' : 'awarded children',
              tone: 'blue'
            }
          ]}
        />

        <ItemsBar
          items={children}
          itemIdList={selectedChildIds}
          onClick={this.onChildPickerClick}
          allItemsTitle="All children"
          getItemName={(child: Child) => `${child.firstName} ${child.lastName}`}
        />

        <div className="parentListControls">
          <button
            type="button"
            className={isFiltersOpen ? 'parentFilterButton is-active' : 'parentFilterButton'}
            onClick={() => this.setState(prev => ({ isFiltersOpen: !prev.isFiltersOpen }))}
          >
            <span className="parentFilterButton__icon" aria-hidden="true" /> Filters
          </button>

          <Pagination2
            modern
            currentPage={currentPage}
            total={total}
            limit={CARD_LIMIT}
            setCurrentPageParams={this.setCurrentPageParams}
          />
        </div>

        {isFiltersOpen && (
          <div className="awardsFilterPanel">
            <ParentFilterBar
              filters={filters}
              onTableFilterChange={this.onTableFilterChange}
              onApply={this.onApplyFilterClick}
              onClear={this.onClearFilterClick}
              onCancel={() => this.setState({ isFiltersOpen: false })}
              labels={{ text: 'Award type', date: 'Awarded date' }}
              fieldKeys={{ textKey: 'awardType.name', dateKey: 'awardedAt' }}
            />
          </div>
        )}

        <section className="awardsListCard">
          <div className="awardsToolbar">
            <div>
              <h2>
                Awards <span>{total}</span>
              </h2>
              <p>
                {isFiltered
                  ? 'Filtered current academic year awards.'
                  : 'Current academic year awards sorted by most recent.'}
              </p>
            </div>
            <div className="awardsToolbar__meta">
              {from}-{to} of {total}
            </div>
          </div>

          {items.length > 0 ? (
            <div className="awardsGrid">
              {items.map(item => (
                <AwardCard item={item} user={this.props.user} key={item.id} />
              ))}
            </div>
          ) : (
            <div className="awardsEmpty">
              <div>★</div>
              <h3>No awards found</h3>
              <p>No awards match the current academic year, child selection and filters.</p>
            </div>
          )}
        </section>
      </main>
    );
  }
}
